import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { toast } from 'react-toastify';

import DataTable from '@/components/DataTable/DataTable';
import EllipsisText from '@/components/EllipsisText';
import { PaymentFilesApi } from '@/api/clients/payment-files';
import { checkPermission } from '@/views/Facilities/CheckPermissions';

const PAYMENT_FILE_TYPES = Object.freeze({
  ACKNOWLEDGEMENT_FILE: 'ACKNOWLEDGEMENT_FILE',
  DAILY_FILE: 'OUTBOUND_REPORT',
});

const TransmittedFilesHistory = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [downloadingFileId, setDownloadingFileId] = useState(null);

  const canDownload =
    checkPermission({
      parentKey: 'refund_reporting',
      childKey: 'transmitted_files',
      permission: 'download',
    }) ||
    checkPermission({
      parentKey: 'refund_reporting',
      childKey: 'refund_reporting',
      permission: 'download',
    });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDownload = useCallback(
    async (file) => {
      setDownloadingFileId(file.id);

      try {
        const response = await PaymentFilesApi.downloadTransmittedFile(file.id);
        PaymentFilesApi.triggerFileDownload(response.data, file.file_name);
        toast.success(t('transmittedFiles.downloadSuccess'));
      } catch (error) {
        console.error('Error downloading file:', error);

        let errorMessage = t('transmittedFiles.downloadError');

        if (error.response?.data instanceof Blob) {
          try {
            const errorText = await error.response.data.text();
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorMessage;
          } catch {
            // Parsing failed, use default message
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        toast.error(errorMessage);
      } finally {
        setDownloadingFileId(null);
      }
    },
    [t],
  );

  const columns = [
    {
      key: 'file_name',
      label: t('transmittedFiles.columns.fileName'),
      sortable: true,
      render: (value) => (
        <EllipsisText text={value || '-'} variant='body2' maxWidth={300} />
      ),
    },
    {
      key: 'created_at',
      label: t('transmittedFiles.columns.transmittedDate'),
      sortable: true,
      format: 'date',
    },
    {
      key: 'total_amount',
      label: t('transmittedFiles.columns.totalAmount'),
      sortable: true,
      align: 'right',
      format: 'currency',
    },
    {
      key: 'no_of_refunds',
      label: t('transmittedFiles.columns.refundCount'),
      sortable: true,
      align: 'right',
      render: (value) => value ?? '-',
    },
    ...(canDownload
      ? [
          {
            key: 'actions',
            label: t('transmittedFiles.columns.actions'),
            sortable: false,
            align: 'center',
            render: (_value, row) => (
              <Tooltip title={t('transmittedFiles.downloadTooltip')}>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(row);
                  }}
                  disabled={downloadingFileId === row.id}>
                  {downloadingFileId === row.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudDownloadIcon />
                  )}
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const getFileTypeForTab = (tabIndex) => {
    return tabIndex === 0
      ? PAYMENT_FILE_TYPES.ACKNOWLEDGEMENT_FILE
      : PAYMENT_FILE_TYPES.DAILY_FILE;
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant='h6'
        sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
        {t('transmittedFiles.title')}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={t('transmittedFiles.tabs.acknowledgementFiles')} />
          <Tab label={t('transmittedFiles.tabs.dailyFiles')} />
        </Tabs>
      </Box>

      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
        }}>
        <DataTable
          key={`transmitted-files-${activeTab}`}
          columns={columns}
          endpoint='/payment-files/transmitted'
          initialFilters={{ file_type: getFileTypeForTab(activeTab) }}
          initialSort={{ key: 'created_at', order: 'desc' }}
          pageSize={10}
          autoFetch={true}
          enableColumnPicker={false}
          enableSorting={true}
          enableFilters={false}
          enablePinning={false}
          tableProps={{
            sx: {
              maxHeight: '400px',
              boxShadow: 'none',
            },
          }}
          renderEmptyState={() => (
            <tr>
              <td
                colSpan={columns.length}
                style={{ textAlign: 'center', padding: '2rem' }}>
                <Typography variant='body2' color='text.secondary'>
                  {t('transmittedFiles.noFiles')}
                </Typography>
              </td>
            </tr>
          )}
        />
      </Box>
    </Box>
  );
};

export default TransmittedFilesHistory;
